import {
  DownloadOutlined,
  ShareAltOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { type Audience, imageFileName, withDownload } from '@horof/shared';
import { Alert, Button, Progress } from 'antd';
import { useEffect, useState } from 'react';

type Prepared = { files: File[] } | { error: string };

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The late-payers images of one audience: open or download each one, or all
 * of them at once. "All at once" fetches the images first (arqam renders each
 * one when asked, which can take a while), then a second tap shares them (the
 * browser only opens the share sheet right after a tap) or downloads them.
 */
export function LatePayersImages({
  audience,
  links,
}: {
  audience: Audience;
  links: string[];
}) {
  const [progress, setProgress] = useState<number | null>(null);
  const [prepared, setPrepared] = useState<Prepared | null>(null);

  // Another audience or new links: start over. (Compared as text: the array
  // itself may be a new object on every render.)
  const linksKey = `${audience}:${links.join('|')}`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when linksKey changes
  useEffect(() => {
    setPrepared(null);
    setProgress(null);
  }, [linksKey]);

  async function prepare() {
    setPrepared(null);
    const date = today();
    const files: File[] = [];
    try {
      for (const [i, link] of links.entries()) {
        setProgress(i);
        const res = await fetch(link);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        files.push(
          new File([blob], imageFileName(audience, date, i, links.length), {
            type: 'image/png',
          }),
        );
      }
      setPrepared({ files });
    } catch (error) {
      console.error('Late-payers images', error);
      setPrepared({
        error: 'تعذّر تحميل الصور. تحقّق من الاتصال وأعد المحاولة.',
      });
    } finally {
      setProgress(null);
    }
  }

  const files = prepared && 'files' in prepared ? prepared.files : null;
  const canShare =
    files !== null &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files });

  async function shareAll() {
    if (!files) return;
    try {
      await navigator.share({ files });
    } catch (error) {
      // Closing the share sheet is not an error.
      if ((error as DOMException).name !== 'AbortError') {
        setPrepared({ error: 'تعذّرت المشاركة. استعمل «تحميل الكل».' });
      }
    }
  }

  function downloadAll() {
    if (!files) return;
    for (const file of files) {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  }

  return (
    <div className="late-images">
      <ul className="plain-list">
        {links.map((link, i) => (
          <li key={link} className="image-row">
            <span>
              الصورة {i + 1} من {links.length}
            </span>
            <span className="image-actions">
              <Button href={link} target="_blank">
                فتح
              </Button>
              <Button
                href={withDownload(link)}
                icon={<DownloadOutlined />}
                aria-label={`تحميل الصورة ${i + 1}`}
              >
                تحميل
              </Button>
            </span>
          </li>
        ))}
      </ul>

      {progress !== null ? (
        <div className="field-hint">
          تجهيز الصورة {progress + 1} من {links.length}…
          <Progress
            percent={Math.round((progress / links.length) * 100)}
            showInfo={false}
          />
        </div>
      ) : files ? (
        <div className="link-grid">
          {canShare && (
            <Button
              type="primary"
              size="large"
              icon={<WhatsAppOutlined />}
              onClick={shareAll}
            >
              مشاركة الكل ({links.length})
            </Button>
          )}
          <Button
            size="large"
            icon={<DownloadOutlined />}
            onClick={downloadAll}
          >
            تحميل الكل ({links.length})
          </Button>
        </div>
      ) : (
        <Button
          size="large"
          block
          icon={<ShareAltOutlined />}
          onClick={prepare}
        >
          تجهيز كل الصور للمشاركة أو التحميل
        </Button>
      )}
      {prepared && 'error' in prepared && (
        <Alert type="error" showIcon message={prepared.error} />
      )}
    </div>
  );
}
